import { useState, useEffect, useCallback } from 'react';
import {
  Activity, RefreshCw, AlertTriangle, CheckCircle, XCircle, X, Zap,
  PhoneCall, Eye, ChevronRight, Clock, Phone, PhoneOff, PhoneMissed,
  MessageSquare, TrendingUp, Lightbulb, FileText, Loader2
} from 'lucide-react';
import api from '@/lib/api';

/* ─── Constants ─────────────────────────────────────────────────────────── */
const SCORE_COLOR  = s => s >= 80 ? '#10b981' : s >= 60 ? '#f59e0b' : '#ef4444';
const SCORE_LABEL  = s => s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Needs Attention' : 'Low';
const SCORE_BG_CLS = s => s >= 80 ? 'bg-emerald-50 border-emerald-200' : s >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

const CLASS_COLOR = {
  ON_CALL: '#10b981', IN_MEETING: '#14b8a6', LUNCH_BREAK: '#3b82f6',
  EOD: '#94a3b8', PASSIVE_WORK: '#eab308', UNEXPLAINED_IDLE: '#f97316', UNPRODUCTIVE: '#ef4444',
};
const CLASS_LABEL = {
  ON_CALL: 'On Call', IN_MEETING: 'In Meeting', LUNCH_BREAK: 'Lunch Break',
  EOD: 'End of Day', PASSIVE_WORK: 'Passive Work', UNEXPLAINED_IDLE: 'Unexplained Idle', UNPRODUCTIVE: 'Unproductive',
};
const CLASS_ICON = {
  ON_CALL: '📞', IN_MEETING: '💬', LUNCH_BREAK: '🍽️',
  EOD: '🌇', PASSIVE_WORK: '💻', UNEXPLAINED_IDLE: '⚠️', UNPRODUCTIVE: '🚩',
};
const REC_COLORS = {
  positive: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', badge: 'bg-emerald-100 text-emerald-700' },
  coaching:  { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-800',   badge: 'bg-blue-100 text-blue-700'   },
  alert:     { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-800',    badge: 'bg-red-100 text-red-700'     },
};

function today() { return new Date().toISOString().split('T')[0]; }

/* ─── Score donut ───────────────────────────────────────────────────────── */
function ScoreDonut({ score }) {
  const r = 36, c = 2 * Math.PI * r;
  const dash = (Math.min(100, Math.max(0, score)) / 100) * c;
  const color = SCORE_COLOR(score);
  return (
    <svg width="100" height="100" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10"/>
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
        strokeDasharray={`${dash} ${c}`} strokeLinecap="round"
        transform="rotate(-90 50 50)" style={{transition:'stroke-dasharray 0.6s ease'}}/>
      <text x="50" y="46" textAnchor="middle" dominantBaseline="middle" fontSize="18" fontWeight="800" fill={color}>{score}</text>
      <text x="50" y="62" textAnchor="middle" fontSize="9" fill="#94a3b8">{SCORE_LABEL(score)}</text>
    </svg>
  );
}

/* ─── Idle timeline ─────────────────────────────────────────────────────── */
function IdleTimeline({ blocks, totalMinutes }) {
  if (!blocks?.length || !totalMinutes) return (
    <div className="h-4 rounded-full bg-slate-100 flex items-center justify-center">
      <span className="text-xs text-slate-400">No idle data</span>
    </div>
  );
  return (
    <div className="flex h-4 rounded-full overflow-hidden w-full gap-px">
      {blocks.map((b, i) => (
        <div key={`${b.classification}-${i}`}
          title={`${CLASS_ICON[b.classification]} ${CLASS_LABEL[b.classification] || b.classification}: ${b.duration_minutes}min`}
          style={{ width: `${Math.max(1.5, (b.duration_minutes / totalMinutes) * 100)}%`, backgroundColor: CLASS_COLOR[b.classification] || '#94a3b8' }}
          className="rounded-sm cursor-help" />
      ))}
    </div>
  );
}

/* ─── Metric tile ───────────────────────────────────────────────────────── */
function MetricTile({ icon: Icon, label, value, sub, color = '#6366f1', flagged = false }) {
  return (
    <div className={`bg-white rounded-2xl p-4 border shadow-sm flex items-start gap-3 ${flagged ? 'border-red-200 bg-red-50/40' : 'border-slate-100'}`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-400 truncate">{label}</div>
        <div className={`text-xl font-black leading-tight ${flagged ? 'text-red-600' : 'text-slate-800'}`}>{value}</div>
        {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

/* ─── Recommendations panel ─────────────────────────────────────────────── */
function InsightsPanel({ recommendations }) {
  if (!recommendations?.length) return null;
  return (
    <div className="space-y-2.5">
      {recommendations.map((rec, i) => {
        const c = REC_COLORS[rec.type] || REC_COLORS.coaching;
        return (
          <div key={rec.title || i} className={`${c.bg} ${c.border} border rounded-2xl px-4 py-3.5 flex items-start gap-3`}>
            <span className="text-xl flex-shrink-0 mt-0.5">{rec.icon}</span>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-bold ${c.text}`}>{rec.title}</div>
              <div className={`text-xs mt-0.5 ${c.text} opacity-80`}>{rec.body}</div>
            </div>
            {rec.action && (
              <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${c.badge}`}>{rec.action}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Transcript modal ──────────────────────────────────────────────────── */
function TranscriptModal({ callId, onClose }) {
  const [data, setData]   = useState(null);
  const [analysis, setAn] = useState(null);
  const [loading, setL]   = useState(true);
  const [analyzing, setA] = useState(false);

  useEffect(() => {
    api.get(`/zoom/transcript/${callId}`)
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ transcript: '', message: 'Failed to load transcript.' }))
      .finally(() => setL(false));
  }, [callId, setData, setL]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAnalysis = async () => {
    setA(true);
    try { const { data: d } = await api.post(`/zoom/analyze/${callId}`); setAn(d); }
    catch (e) { setAn({ error: e?.response?.data?.detail || 'Analysis failed' }); }
    finally { setA(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white/95 border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-500" />
            <h2 className="font-black text-slate-800">Call Transcript</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
          ) : (
            <>
              {/* Transcript */}
              <div className="bg-slate-50 rounded-2xl p-4 max-h-64 overflow-auto">
                {data?.transcript ? (
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{data.transcript}</pre>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">{data?.message || 'No transcript available for this call.'}</p>
                )}
              </div>
              {/* AI Analysis */}
              {data?.transcript && !analysis && (
                <button onClick={runAnalysis} disabled={analyzing}
                  className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-sm rounded-2xl transition-colors border border-indigo-100">
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
                  {analyzing ? 'Analyzing...' : 'Run AI Analysis'}
                </button>
              )}
              {analysis && !analysis.error && (
                <div className="space-y-3">
                  <h3 className="text-sm font-bold text-slate-700">AI Analysis</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: 'Sentiment', value: analysis.sentiment || '—' },
                      { label: 'Interest', value: analysis.candidate_interest || analysis.prospect_interest || '—' },
                      { label: 'Quality Score', value: analysis.call_quality_score ? `${analysis.call_quality_score}/100` : '—' },
                      { label: 'Talk Ratio', value: analysis.talk_ratio ? `You ${analysis.talk_ratio.recruiter || analysis.talk_ratio.sales || 0}%` : '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
                        <div className="text-sm font-black text-slate-800">{value}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{label}</div>
                      </div>
                    ))}
                  </div>
                  {analysis.coaching_tip && (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                      <p className="text-xs font-semibold text-blue-700">💡 {analysis.coaching_tip}</p>
                    </div>
                  )}
                  {analysis.next_action && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                      <p className="text-xs font-semibold text-emerald-700">✅ Next: {analysis.next_action}</p>
                    </div>
                  )}
                  {analysis.objections_raised?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {analysis.objections_raised.map((o, i) => (
                        <span key={o} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">⚠️ {o}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Call history tab ──────────────────────────────────────────────────── */
function CallHistoryTab({ date }) {
  const [calls, setCalls]   = useState([]);
  const [loading, setL]     = useState(true);
  const [txModal, setTxM]   = useState(null);
  const [calling, setCalling] = useState(null);

  useEffect(() => {
    api.get(`/zoom/call-history?date=${date}`)
      .then(({ data }) => setCalls(data.calls || []))
      .catch(() => setCalls([]))
      .finally(() => setL(false));
  }, [date, setCalls, setL]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>;
  if (!calls.length) return (
    <div className="text-center py-16 bg-slate-50 rounded-2xl">
      <PhoneOff className="w-10 h-10 text-slate-200 mx-auto mb-3" />
      <p className="text-sm font-bold text-slate-500">No calls on {date}</p>
      <p className="text-xs text-slate-400 mt-1">Calls made via Zoom Phone will appear here.</p>
    </div>
  );

  return (
    <div className="space-y-2">
      {calls.map((call, i) => {
        const cid      = call.call_id || call.id || '';
        const dir      = call.direction || 'outbound';
        const result   = call.result || 'unknown';
        const durMins  = Math.round((call.duration || 0) / 60);
        const answered = result === 'answered';
        return (
          <div key={call.call_id || call.id || i} className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-3 flex items-center gap-4">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${answered ? 'bg-emerald-50' : 'bg-red-50'}`}>
              {answered ? <PhoneCall className="w-4 h-4 text-emerald-600" /> : <PhoneMissed className="w-4 h-4 text-red-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-slate-800 truncate">{call.callee_name || call.caller_name || call.callee_did || call.callee_number || 'Unknown'}</div>
              <div className="text-xs text-slate-400">{dir} · {durMins}min · {(call.start_time || '').slice(11,16)}</div>
            </div>
            <div className="flex items-center gap-2">
              {call.has_transcript && (
                <button onClick={() => setTxM(cid)}
                  className="flex items-center gap-1 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-xl transition-colors border border-indigo-100">
                  <FileText className="w-3 h-3" /> Transcript
                </button>
              )}
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${answered ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                {result}
              </span>
            </div>
          </div>
        );
      })}
      {txModal && <TranscriptModal callId={txModal} onClose={() => setTxM(null)} />}
    </div>
  );
}

/* ─── Click-to-Call button (used in candidate detail) ───────────────────── */
export function ClickToCallButton({ phone, candidateId }) {
  const [calling, setCalling] = useState(false);
  const [done, setDone]       = useState(false);
  if (!phone) return null;
  const handleCall = async () => {
    setCalling(true);
    try {
      await api.post('/zoom/call', { phone_number: phone, candidate_id: candidateId });
      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Click-to-call failed. Check Zoom integration in Settings.');
    } finally { setCalling(false); }
  };
  return (
    <button onClick={handleCall} disabled={calling}
      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors border
        ${done ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'}`}>
      {calling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
      {done ? 'Calling...' : calling ? 'Dialing' : 'Call'}
    </button>
  );
}

/* ─── Detail modal ──────────────────────────────────────────────────────── */
function DetailModal({ report, onClose }) {
  const [tab, setTab] = useState('overview');
  if (!report) return null;
  const blocks  = report.idle_analysis?.classified_blocks || [];
  const topApps = report.trackabi?.top_apps || [];
  const zoom    = report.zoom || {};
  const tb      = report.trackabi || {};
  const idle    = report.idle_analysis || {};
  const score   = report.productivity_score ?? 0;
  const recs    = report.recommendations || [];

  const idleSummary = {};
  blocks.forEach(b => { if (!idleSummary[b.classification]) idleSummary[b.classification] = 0; idleSummary[b.classification] += b.duration_minutes; });

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'idle', label: 'Idle Blocks' },
    { id: 'calls', label: `Calls (${zoom.total_calls ?? 0})` },
    { id: 'insights', label: `Insights ${recs.length ? `(${recs.length})` : ''}` },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white text-lg font-black" style={{ backgroundColor: SCORE_COLOR(score) }}>
              {(report.name || '?').charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="font-black text-slate-800 text-lg leading-tight">{report.name}</h2>
              <p className="text-xs text-slate-400">{report.email} · {report.date}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-sm font-semibold px-4 py-3 border-b-2 transition-colors ${tab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-4">
          {tab === 'overview' && (
            <>
              <div className="flex items-center gap-6">
                <ScoreDonut score={score} />
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <MetricTile icon={PhoneCall} label="Total Calls" value={zoom.total_calls ?? 0} sub={`${zoom.outbound_calls ?? 0} outbound`} color="#6366f1" />
                  <MetricTile icon={Clock} label="Talk Time" value={`${zoom.total_talk_minutes ?? 0}m`} sub={`${zoom.answered_calls ?? 0} answered`} color="#10b981" />
                  <MetricTile icon={Activity} label="Active Time" value={`${tb.total_active_minutes ?? 0}m`} sub={`${tb.raw_activity_score ?? 0}% activity`} color="#3b82f6" />
                  <MetricTile icon={AlertTriangle} label="Unexplained Idle" value={`${idle.unexplained_minutes ?? 0}m`} sub={`of ${idle.total_idle_minutes ?? 0}m idle`} color="#ef4444" flagged={(idle.unexplained_minutes ?? 0) > 30} />
                </div>
              </div>
              <IdleTimeline blocks={blocks} totalMinutes={tb.total_logged_minutes} />
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2">
                {Object.entries(idleSummary).map(([k, mins]) => (
                  <div key={k} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CLASS_COLOR[k] || '#94a3b8' }}/>
                    {CLASS_ICON[k]} {CLASS_LABEL[k] || k}: <strong>{mins}m</strong>
                  </div>
                ))}
              </div>
              {topApps.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-slate-700 mb-3">💻 Top Apps</h3>
                  <div className="space-y-2">
                    {topApps.map((app, i) => {
                      const pct = Math.round((app.duration_seconds / (topApps[0]?.duration_seconds || 1)) * 100);
                      return (
                        <div key={app.app_name || i} className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">{i+1}</div>
                          <div className="w-32 text-sm text-slate-700 truncate">{app.app_name}</div>
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div className="h-2 rounded-full" style={{ width:`${pct}%`, backgroundColor: i===0?'#6366f1':i===1?'#3b82f6':'#94a3b8' }}/>
                          </div>
                          <div className="text-xs text-slate-500 w-12 text-right">{Math.round(app.duration_seconds/60)}m</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'idle' && (
            blocks.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-2xl"><p className="text-2xl mb-2">✅</p><p className="text-sm text-slate-500">No idle blocks detected</p></div>
            ) : (
              <div className="rounded-2xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50"><tr>{['Time Window','Duration','Classification','Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500">{h}</th>
                  ))}</tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {blocks.map((b, i) => (
                      <tr key={b.start || i} className={b.is_flagged ? 'bg-red-50/50' : 'hover:bg-slate-50'}>
                        <td className="px-4 py-3 text-xs font-mono text-slate-600">{(b.start||'').slice(11,16)} – {(b.end||'').slice(11,16)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-slate-700">{b.duration_minutes}m</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full"
                            style={{ backgroundColor:`${CLASS_COLOR[b.classification]||'#94a3b8'}20`, color:CLASS_COLOR[b.classification]||'#64748b' }}>
                            {CLASS_ICON[b.classification]} {CLASS_LABEL[b.classification]||b.classification}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {b.is_flagged
                            ? <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Flagged 🚩</span>
                            : <span className="text-xs font-semibold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">OK ✓</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === 'calls' && <CallHistoryTab date={report.date} />}

          {tab === 'insights' && (
            recs.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-2xl">
                <Lightbulb className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No recommendations yet</p>
              </div>
            ) : <InsightsPanel recommendations={recs} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Recruiter card ────────────────────────────────────────────────────── */
function RecruiterCard({ report, onViewDetails }) {
  const score = report.productivity_score ?? 0;
  const idle  = report.idle_analysis || {};
  const zoom  = report.zoom || {};
  const tb    = report.trackabi || {};
  const recs  = report.recommendations || [];
  const alerts = recs.filter(r => r.type === 'alert').length;

  if (report.error) return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <p className="text-sm font-bold text-slate-700">{report.email}</p>
      <p className="text-xs text-red-500 mt-1">{report.error}</p>
    </div>
  );

  const flagCount = idle.flagged_blocks?.length || 0;
  return (
    <div className={`bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer ${SCORE_BG_CLS(score)}`} onClick={() => onViewDetails(report)}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-black text-base" style={{ backgroundColor: SCORE_COLOR(score) }}>
            {(report.name||'?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-black text-slate-800 leading-tight">{report.name}</div>
            <div className="text-xs text-slate-400 truncate max-w-[130px]">{report.email}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black leading-none" style={{ color: SCORE_COLOR(score) }}>{score}</div>
          <div className="text-xs font-medium mt-0.5" style={{ color: SCORE_COLOR(score) }}>{SCORE_LABEL(score)}</div>
          {alerts > 0 && <div className="text-xs text-red-600 font-bold mt-1">🚩 {alerts} alert{alerts>1?'s':''}</div>}
        </div>
      </div>
      <div className="mb-3 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-2 rounded-full transition-all duration-700" style={{ width:`${score}%`, backgroundColor: SCORE_COLOR(score) }}/>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="bg-white/80 rounded-xl py-2 border border-slate-100">
          <div className="text-base font-black text-indigo-600">{zoom.total_calls ?? 0}</div>
          <div className="text-xs text-slate-400">calls</div>
        </div>
        <div className="bg-white/80 rounded-xl py-2 border border-slate-100">
          <div className="text-base font-black text-emerald-600">{zoom.total_talk_minutes ?? 0}m</div>
          <div className="text-xs text-slate-400">talk time</div>
        </div>
        <div className={`rounded-xl py-2 border ${flagCount > 0 ? 'bg-red-50 border-red-200' : 'bg-white/80 border-slate-100'}`}>
          <div className={`text-base font-black ${flagCount > 0 ? 'text-red-500' : 'text-slate-600'}`}>{idle.unexplained_minutes ?? 0}m</div>
          <div className="text-xs text-slate-400">idle</div>
        </div>
      </div>
      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate-400 mb-1.5">
          <span>⌨️ {tb.total_active_minutes ?? 0}m active</span>
          <span>{tb.raw_activity_score ?? 0}% activity</span>
          {flagCount > 0 && <span className="text-red-500 font-semibold">🚩 {flagCount} flags</span>}
        </div>
        <IdleTimeline blocks={idle.classified_blocks} totalMinutes={tb.total_logged_minutes} />
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-slate-100/80">
        <span className="text-xs text-slate-400">{tb.total_logged_minutes ?? 0}m logged</span>
        <span className="text-xs font-semibold text-indigo-600 flex items-center gap-1">View details <ChevronRight className="w-3 h-3" /></span>
      </div>
    </div>
  );
}

/* ─── My Productivity (self-view) ───────────────────────────────────────── */
function MyProductivityView({ myReport, loading, onRefresh, date, onDateChange }) {
  const [tab, setTab]   = useState('overview');
  const [modal, setModal] = useState(false);

  if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" /></div>;
  if (!myReport || myReport.error) return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <Activity className="w-12 h-12 text-slate-200 mb-4" />
      <h3 className="text-base font-bold text-slate-600">No data yet</h3>
      <p className="text-sm text-slate-400 mt-1 max-w-xs">{myReport?.error || 'Productivity data appears once Trackabi + Zoom are configured.'}</p>
    </div>
  );

  const score = myReport.productivity_score ?? 0;
  const zoom  = myReport.zoom || {};
  const tb    = myReport.trackabi || {};
  const idle  = myReport.idle_analysis || {};
  const recs  = myReport.recommendations || [];

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'calls', label: `Calls (${zoom.total_calls ?? 0})` },
    { id: 'insights', label: `Insights ${recs.length ? `(${recs.length})` : ''}` },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-slate-800">My Productivity</h2>
          <p className="text-sm text-slate-400">Personal summary for {date}</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => onDateChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
          <button onClick={onRefresh} className="p-2 hover:bg-slate-100 rounded-xl border border-slate-200 bg-white">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Hero score */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-8">
          <ScoreDonut score={score} />
          <div className="flex-1">
            <h3 className="text-2xl font-black text-slate-800">{SCORE_LABEL(score)} Day</h3>
            <p className="text-sm text-slate-400 mt-1 mb-4">
              {score >= 80 ? "You're crushing it today 🔥" : score >= 60 ? "Solid work today — keep going." : "Check your insights below for coaching tips."}
            </p>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div className="h-3 rounded-full transition-all duration-700" style={{ width:`${score}%`, backgroundColor: SCORE_COLOR(score) }}/>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricTile icon={PhoneCall} label="Total Calls" value={zoom.total_calls ?? 0} sub={`${zoom.outbound_calls ?? 0} outbound`} color="#6366f1" />
        <MetricTile icon={Clock} label="Talk Time" value={`${zoom.total_talk_minutes ?? 0}m`} sub={`${zoom.answered_calls ?? 0} answered · ${zoom.missed_calls ?? 0} missed`} color="#10b981" />
        <MetricTile icon={Activity} label="Active Time" value={`${tb.total_active_minutes ?? 0}m`} sub={`${tb.raw_activity_score ?? 0}% activity score`} color="#3b82f6" />
        <MetricTile icon={AlertTriangle} label="Unexplained Idle" value={`${idle.unexplained_minutes ?? 0}m`} sub={`of ${idle.total_idle_minutes ?? 0}m idle`} color="#ef4444" flagged={(idle.unexplained_minutes ?? 0) > 30} />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100 px-2">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-sm font-semibold px-4 py-3.5 border-b-2 transition-colors ${tab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'overview' && (
            <div className="space-y-4">
              <IdleTimeline blocks={idle.classified_blocks} totalMinutes={tb.total_logged_minutes} />
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {Object.entries(CLASS_LABEL).map(([k, v]) => {
                  const mins = (idle.classified_blocks||[]).filter(b=>b.classification===k).reduce((s,b)=>s+b.duration_minutes,0);
                  if (!mins) return null;
                  return (
                    <div key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                      <div className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:CLASS_COLOR[k]}}/>
                      {CLASS_ICON[k]} {v}: <strong>{mins}m</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {tab === 'calls'    && <CallHistoryTab date={date} />}
          {tab === 'insights' && (recs.length ? <InsightsPanel recommendations={recs} /> : (
            <div className="text-center py-10 text-slate-400">
              <Lightbulb className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No insights yet — check back after your first calls.</p>
            </div>
          ))}
        </div>
      </div>

      <button onClick={() => setModal(true)}
        className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-sm rounded-2xl transition-colors border border-indigo-100">
        <Eye className="w-4 h-4" /> Full Detail View
      </button>
      {modal && <DetailModal report={myReport} onClose={() => setModal(false)} />}
    </div>
  );
}

/* ─── Admin team view ───────────────────────────────────────────────────── */
function AdminTeamView({ teamData, status, loading, syncing, onSync, onRefresh, onViewDetails, date, onDateChange }) {
  if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" /></div>;

  const members        = teamData?.team || [];
  const avgScore       = teamData?.team_avg_score ?? 0;
  const topPerformer   = [...members].sort((a,b)=>(b.productivity_score??0)-(a.productivity_score??0))[0];
  const flaggedMembers = members.filter(m => (m.idle_analysis?.flagged_blocks?.length||0) > 0);
  const alertMembers   = members.filter(m => (m.recommendations||[]).some(r=>r.type==='alert'));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-slate-800">Team Productivity</h2>
          <p className="text-sm text-slate-400">Trackabi × Zoom correlated — {date}</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => onDateChange(e.target.value)}
            className="text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-white" />
          <button onClick={onRefresh} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 shadow-sm transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`} /> Refresh
          </button>
          <button onClick={onSync} disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-xl shadow-md"
            style={{ background:'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
            <Zap className={`w-4 h-4 ${syncing?'animate-pulse':''}`} /> {syncing ? 'Syncing...' : 'Sync Team'}
          </button>
        </div>
      </div>

      {status && (status.unsynced > 0 ? (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3.5">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800"><strong>{status.unsynced} member(s)</strong> not synced. Go to <strong>Settings → Integrations</strong> and click <strong>Sync Team</strong>.</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3.5">
          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          <p className="text-sm text-emerald-800">All <strong>{status.synced}</strong> team members synced.</p>
        </div>
      ))}

      {members.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
            <div className="text-3xl font-black mb-0.5" style={{ color: SCORE_COLOR(avgScore) }}>{avgScore}</div>
            <div className="text-xs text-slate-400">Team Avg Score</div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-2 rounded-full" style={{ width:`${avgScore}%`, backgroundColor: SCORE_COLOR(avgScore) }}/>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
            <div className="text-3xl font-black text-indigo-600 mb-0.5">{members.length}</div>
            <div className="text-xs text-slate-400">Members Tracked</div>
            <div className="text-xs text-emerald-600 font-semibold mt-1">{members.filter(m=>(m.productivity_score??0)>=80).length} excellent</div>
          </div>
          <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 text-center">
            <div className="text-sm font-black text-indigo-600 leading-tight truncate">{topPerformer?.name || '—'}</div>
            <div className="text-xs text-slate-400 mt-0.5">🏆 Top Performer</div>
            {topPerformer && <div className="text-xl font-black mt-1" style={{ color: SCORE_COLOR(topPerformer.productivity_score??0) }}>{topPerformer.productivity_score??0}</div>}
          </div>
          <div className={`rounded-2xl border shadow-sm p-4 text-center ${alertMembers.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`}>
            <div className={`text-3xl font-black mb-0.5 ${alertMembers.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>{alertMembers.length}</div>
            <div className="text-xs text-slate-400">Need Attention</div>
            {alertMembers.length > 0 && <div className="text-xs text-red-500 font-semibold mt-1">🚩 coach today</div>}
          </div>
        </div>
      )}

      {members.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((r) => <RecruiterCard key={r.user_id || r.id || r.email} report={r} onViewDetails={onViewDetails} />)}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-slate-100">
          <Activity className="w-12 h-12 text-slate-200 mb-4" />
          <h3 className="text-base font-bold text-slate-600">No team data yet</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-xs">{teamData?.warning || 'Add Trackabi + Zoom keys in Settings → Integrations, then Sync Team.'}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2">
        {Object.entries(CLASS_LABEL).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CLASS_COLOR[k] }}/>{CLASS_ICON[k]} {v}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Root ──────────────────────────────────────────────────────────────── */
export default function ProductivityDashboard() {
  const [date, setDate]         = useState(today());
  const [teamData, setTeamData] = useState(null);
  const [myReport, setMyReport] = useState(null);
  const [status, setStatus]     = useState(null);
  const [isAdmin, setIsAdmin]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError]       = useState('');

  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      setIsAdmin(['owner','org_admin'].includes(data.workspace_role_raw || data.workspace_role || ''));
    }).catch(() => setIsAdmin(false));
  }, []);

  const fetchStatus = useCallback(async () => {
    try { const { data } = await api.get('/productivity/integration-status'); setStatus(data); }
    catch (err) { logger.error('[ProductivityDashboard] fetchStatus failed:', err?.message); }
  }, []);

  const fetchTeam = useCallback(async (d) => {
    setLoading(true); setError('');
    try { const { data } = await api.get(`/productivity/team?date=${d}`); setTeamData(data); }
    catch (e) { setError(e?.response?.data?.detail || 'Failed to load team data'); }
    finally { setLoading(false); }
  }, []);

  const fetchMine = useCallback(async (d) => {
    setLoading(true); setError('');
    try { const { data } = await api.get(`/productivity/my-report?date=${d}`); setMyReport(data); }
    catch (e) { setError(e?.response?.data?.detail || 'Failed to load your data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStatus();
    if (isAdmin) fetchTeam(date); else fetchMine(date);
  }, [isAdmin, date, fetchStatus, fetchTeam, fetchMine]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/productivity/sync-team');
      await fetchStatus();
      alert(`✅ Synced ${data.synced_count ?? 0} members. ${data.failed_count ?? 0} failed.`);
    } catch (e) { alert(`Sync failed: ${e?.response?.data?.detail || e.message}`); }
    finally { setSyncing(false); }
  };

  const handleDateChange = (d) => {
    setDate(d);
    if (isAdmin) fetchTeam(d); else fetchMine(d);
  };

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-md" style={{ background:'linear-gradient(135deg,#6366f1,#4f46e5)' }}>
            <Activity className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Productivity Intelligence</h1>
            <p className="text-xs text-slate-400">Trackabi keyboard/mouse × Zoom calls × AI coaching — correlated in real time</p>
          </div>
        </div>
        {error && (
          <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-3.5">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {isAdmin ? (
          <AdminTeamView teamData={teamData} status={status} loading={loading} syncing={syncing}
            onSync={handleSync} onRefresh={() => fetchTeam(date)} onViewDetails={setSelected}
            date={date} onDateChange={handleDateChange} />
        ) : (
          <MyProductivityView myReport={myReport} loading={loading}
            onRefresh={() => fetchMine(date)} date={date} onDateChange={handleDateChange} />
        )}
      </div>
      {selected && <DetailModal report={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
